import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAppFilters } from "../state/filters";
import { KpiCard, KpiSkeleton } from "../components/KpiCard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { formatInt, formatMoney, formatPct } from "../lib/format";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { DollarSign, TrendingUp, ShoppingCart, Users, CheckCircle2, AlertTriangle, Activity, PieChart as PieIcon, ShieldCheck } from "lucide-react";

const CHART_COLORS = ["hsl(198 88% 55%)", "hsl(158 64% 45%)", "hsl(38 92% 60%)", "hsl(262 35% 70%)", "hsl(330 70% 62%)", "hsl(215 20% 70%)"];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <div className="text-muted-foreground mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 tabular-nums">
          <span className="inline-block size-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span className="text-foreground">{p.name}:</span>
          <span className="text-foreground">{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function ExecutiveDashboard() {
  const { range, rep, source } = useAppFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/dashboard/executive", {
          params: { start: range.start, end: range.end, rep: rep || undefined, source: source || undefined },
        });
        if (!dead) setData(data);
      } finally {
        if (!dead) setLoading(false);
      }
    };
    load();
    const h = () => load();
    window.addEventListener("cwcc:refresh", h);
    return () => { dead = true; window.removeEventListener("cwcc:refresh", h); };
  }, [range, rep, source]);

  const k = data?.kpis || {};
  return (
    <div className="space-y-6" data-testid="page-executive">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Executive Dashboard</h1>
          <p className="text-sm text-muted-foreground">Revenue, pipeline health, and data integrity at a glance.</p>
        </div>
      </div>

      {loading ? <KpiSkeleton n={6} /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          <KpiCard label="New Revenue" value={formatMoney(k.new_revenue)} delta={`${formatInt(k.total_sales)} sales`} icon={DollarSign} tone="positive" testid="kpi-new-revenue" />
          <KpiCard label="Billed" value={formatMoney(k.billed_revenue)} delta={`Collected ${formatMoney(k.collected_revenue)}`} icon={TrendingUp} testid="kpi-billed" />
          <KpiCard label="Avg Deal" value={formatMoney(k.avg_deal_size)} delta={`${k.won_opportunities} won`} icon={ShoppingCart} testid="kpi-avg-deal" />
          <KpiCard label="Close Rate" value={formatPct(k.close_rate)} delta={`${k.lost_opportunities} lost`} icon={CheckCircle2} tone={k.close_rate >= 50 ? "positive" : "negative"} testid="kpi-close-rate" />
          <KpiCard label="Show Rate" value={formatPct(k.show_rate)} delta={`${k.completed_appointments}/${k.total_appointments} appts`} icon={Users} testid="kpi-show-rate" />
          <KpiCard label="Open Pipeline" value={formatMoney(k.open_pipeline_value)} delta={`${k.open_opportunities} open opps`} icon={Activity} testid="kpi-open-pipeline" />
          <KpiCard label="Health Score" value={formatInt(k.health_score)} delta={`Match ${formatPct(k.matched_pct)}`} icon={ShieldCheck} tone={k.health_score >= 80 ? "positive" : k.health_score >= 60 ? "neutral" : "negative"} testid="kpi-health-score" />
          <KpiCard label="Open Issues" value={formatInt(k.unresolved_discrepancies)} delta="discrepancies" icon={AlertTriangle} tone={k.unresolved_discrepancies > 20 ? "negative" : "neutral"} testid="kpi-open-issues" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-8" data-testid="card-trend">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Revenue Trend · Last 12 weeks</CardTitle>
            <span className="text-xs text-muted-foreground">New revenue vs billed</span>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[260px] w-full" /> : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.trend_weekly} margin={{ top: 6, right: 12, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(198 88% 55%)" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(198 88% 55%)" stopOpacity={0} /></linearGradient>
                    <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(158 64% 45%)" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(158 64% 45%)" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="week_start" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area name="New revenue" type="monotone" dataKey="new_revenue" stroke="hsl(198 88% 55%)" fill="url(#g1)" strokeWidth={2} />
                  <Area name="Billed" type="monotone" dataKey="billed" stroke="hsl(158 64% 45%)" fill="url(#g2)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-4" data-testid="card-by-source">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Revenue by source</CardTitle>
            <PieIcon size={14} className="text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[260px] w-full" /> : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={data.revenue_by_source.slice(0, 6)} dataKey="revenue" nameKey="source" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {(data.revenue_by_source || []).slice(0, 6).map((entry, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-8" data-testid="card-by-rep">
          <CardHeader><CardTitle className="text-sm font-medium">Revenue by rep</CardTitle></CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[240px] w-full" /> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={(data.revenue_by_rep || []).slice(0, 10)} margin={{ top: 6, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="rep" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="revenue" fill="hsl(198 88% 55%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-4" data-testid="card-health">
          <CardHeader><CardTitle className="text-sm font-medium">Data integrity</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Matched records</span><span className="font-display font-semibold">{formatPct(k.matched_pct)}</span></div>
              <div className="h-2 mt-2 rounded-full bg-[hsl(var(--surface-3))] overflow-hidden"><div className="h-full bg-[hsl(var(--positive))]" style={{ width: `${k.matched_pct || 0}%` }} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Health score</span><span className="font-display font-semibold">{formatInt(k.health_score)}</span></div>
              <div className="h-2 mt-2 rounded-full bg-[hsl(var(--surface-3))] overflow-hidden"><div className="h-full bg-[hsl(var(--info))]" style={{ width: `${Math.min(100, k.health_score || 0)}%` }} /></div>
            </div>
            <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
              <span className="text-muted-foreground flex items-center gap-2"><AlertTriangle size={14} className="text-[hsl(var(--warning))]" /> Open discrepancies</span>
              <span className="font-display font-semibold">{formatInt(k.unresolved_discrepancies)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
